import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import FormController from '../../common/formController';
import { isArray, isObject } from 'lodash';
import { actions } from '../../store/schema';
import Joi from 'joi';
import validators from './validators';
import { getEnvVar } from 'helpers';

const {
  getSchema,
  getSchemaKeys,
  createSchema,
  updateSchema,
  setCurrentSchema,
  setCurrentSchemaDetails,
  loadingCurrentSchema,
  creatingSchemaFailed
} = actions;

let formController = new FormController();

export default function useSchema(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { schemaId } = useParams();

  const schema = useSelector(store => store.schema.current);
  const enrichmentSchemas = useSelector(store => store.enrichedSchema.list);
  const schemaDetails = useSelector(store => store.schema.currentDetails);
  const schemaKeys = useSelector(store => store.schema.keys);
  const isLoading = useSelector(store => store.schema.getting);
  const schemaSaveFailed = useSelector(store => store.schema.schemaSaveFailed);
  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const [schemaObject, setSchemaObject] = useState({});
  const [schemaObjectCopy, setSchemaObjectCopy] = useState({});
  const [isInvalidPayload, setIsInvalidPayload] = useState(false);
  const [formState, setFormState] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [enrichmentProducts, setEnrichmentProducts] = useState([]);

  const disabledInputs = [
    'tenantId',
    'businessContextId',
    'eventProductCategory',
    'eventProductName',
    'eventTypeCategory'
  ];
  const requiredFields = [
    ...disabledInputs,
    'eventTypeGroup',
    'eventTypeIdentifierValue',
    'eventTypeSchema',
    'schemaName',
    'schemaToEventPartialCollectionName',
    'eventTypePath',
    'enrichmentProduct',
    'enrichmentProductUuidPath',
    'schemaEnrichmentUuid'
  ];

  useEffect(() => {
    let products =
      (getEnvVar('ADM_ENRICHMENT_PRODUCTS'))?.split(',') || [];
    setEnrichmentProducts(products);
    if (schemaId) {
      dispatch(getSchema({ force: true, schemaId }));
      formController = new FormController();
    } else {
      if (Object.keys(schema).length > 0) {
        formController = new FormController();
        setSchemaObject(schemaObject =>
          createObjectFromValidSchema({ ...schemaObject, ...schema })
        );
      }
    }
  }, []);

  useEffect(() => {
    if (schemaSaveFailed) {
      setSchemaObject(schemaObject =>
        createObjectFromValidSchema({ ...schemaObject, ...schema })
      );
      dispatch(loadingCurrentSchema());
    }
  }, [schemaSaveFailed]);

  useEffect(() => {
    if (schemaKeys) {
      let schemaObj = createObjectFromSchema(schemaKeys, '') || {};
      setSchemaObject(schemaObject =>
        createObjectFromValidSchema({ ...schemaObj, ...(JSON.parse(JSON.stringify(schemaObject))) })
      );
      let schemaObjCopy = createObjectFromSchema(schemaKeys, '', true) || {};
      setSchemaObjectCopy(JSON.parse(JSON.stringify(schemaObjCopy)));
    }
  }, [schemaKeys]);

  useEffect(() => {
    if (schema) {
      setSchemaObject(schemaObject =>
        createObjectFromValidSchema({ ...schemaObject, ...schema })
      );
    }
  }, [schema]);

  const navBackOnError = path => {
    if ((!schemaDetails.name || !schemaDetails.option) && !schema.id) {
      history.push(path);
    }
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(
      segment === 'details' ? schemaDetails : schemaObject,
      {
        abortEarly: false,
        allowUnknown: true
      }
    );
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  const getCurrentSchemaKeys = () => {
    dispatch(getSchemaKeys());
  };

  const createStringFromSchema = (objectJson, key) => {
    let schemaOptions =
      schemaDetails.option && schemaDetails.option.split(' - ');
    switch (key) {
      case 'businessContextId':
        objectJson[key] = currentBusinessContextId;
        break;
      case 'tenantId':
        objectJson[key] = currentTenantId;
        break;
      case 'eventProductCategory':
        objectJson[key] = (schemaOptions && schemaOptions[0]) || '';
        break;
      case 'eventProductName':
        objectJson[key] = (schemaOptions && schemaOptions[1]) || '';
        break;
      case 'eventTypeCategory':
        objectJson[key] = (schemaOptions && schemaOptions[2]) || '';
        break;
      case 'schemaName':
        objectJson[key] = schemaDetails.name;
        break;
      case 'id':
        break;
      default:
        objectJson[key] = '';
        break;
    }
    return objectJson;
  };
  function CreateArrayobjectFromSchema(value, objectJson, parentKeyPath, key, includeArrayElements) {
    switch (value.items.type) {
      case 'object': objectJson = [
        createObjectFromSchema(
          value.items.properties,
          parentKeyPath + key + '.',
          includeArrayElements
        )
      ];
        break;
      case 'string': objectJson = [''];
        break;
    }
    return objectJson;
  }
  const createObjectFromSchema = (object, parentKeyPath, includeArrayElements) => {
    let objectJson = {};
    let keys = parentKeyPath.split('.');
    Object.entries(object).forEach(entry => {
      let key = entry[0];
      let value = entry[1];
      switch (value.type) {
        case 'string':
          objectJson = createStringFromSchema(objectJson, key); break;
        case 'boolean': {
          if (
            (keys.indexOf('eventListFields') >= 0 && key === 'campaignVisible') ||
            key === 'templateVisible'
          ) {
            objectJson[key] = true;
          } else {
            objectJson[key] =
              key === 'eventTypeDefault' ? schemaDetails.isDefaultSchema : false;
          }
          break;
        }
        case 'array': {
          objectJson[key] = includeArrayElements ? CreateArrayobjectFromSchema(value, objectJson[key], parentKeyPath, key, includeArrayElements) : [];
          break;
        }
        default:
          break;
      }
    });
    return objectJson;
  };

  const createObjectFromValidSchema = object => {
    let objectJson = {};
    Object.entries(object).forEach(entry => {
      let key = entry[0];
      let value = entry[1];
      if (isArray(value)) {
        objectJson[key] = [];
        value.map(obj => {
          objectJson[key].push(
            isObject(obj) ? createObjectFromValidSchema(obj) : obj
          );
        });
      } else if (key === 'schemaName') {
        objectJson[key] = object.schemaName;
      } else if (key !== 'id') {
        objectJson[key] = value;
      }
    });
    return objectJson;
  };

  const shouldShowError = name => {
    let key = name?.split('.');
    if (key.indexOf('eventTypeUniqueUpdateKey') >= 0) {
      return formState[name]?.touched;
    } else if (key.indexOf('eventTypeGroup') >= 0) {
      return (
        formState[name]?.touched &&
        formController.getErrorMessage(validationErrors, name) &&
        formController.getErrorMessage(validationErrors, name) !== ''
      );
    } else {
      return formController.shouldShowError(validationErrors, name);
    }
  };

  const getErrorMessage = name => {
    let key = name?.split('.');
    if (key.indexOf('eventTypeUniqueUpdateKey') >= 0) {
      return formState[name]?.message;
    } else if (key.indexOf('eventTypeGroup') >= 0) {
      return (
        formState[name]?.touched &&
        formController.getErrorMessage(validationErrors, name)
      );
    } else {
      return formController.getErrorMessage(validationErrors, name);
    }
  };

  const validateJsonPayload = () => {
    try {
      schemaObject['examplePayload'] !== '' &&
        JSON.parse(schemaObject['examplePayload']);
      setIsInvalidPayload(false);
      return true;
    } catch (ex) {
      setIsInvalidPayload(true);
      return false;
    }
  };

  const validateEventType = parentKey => {
    Object.keys(formState)
      .filter(k => k.indexOf(parentKey) >= 0)
      .forEach(
        entry =>
        (formState[entry] = {
          touched: false
        })
      );
  };

  const validateKeys = () => {
    Object.keys(formState)
      .filter(key => key.split('.').indexOf('eventTypeUniqueUpdateKey') >= 0)
      .forEach(key => {
        formState[key] = { touched: false, message: '' };
      });
    let eventFieldValues = schemaObject.eventFields.map(key => key.name);
    let errors = {};
    schemaObject.eventTypeUniqueUpdateKey &&
      schemaObject.eventTypeUniqueUpdateKey.map((key, i) => {
        if (eventFieldValues.indexOf(key) < 0 && key !== '') {
          formState['eventTypeUniqueUpdateKey.' + i] = {
            touched: true,
            message:
              "Invalid key. Please enter a key from 'name' of [eventFields]"
          };
          errors['eventTypeUniqueUpdateKey.' + i] = {
            showError: true,
            message:
              "Invalid key. Please enter a key from 'name' of [eventFields]"
          };
        }
      });
    return errors;
  };

  const handleChange = event => {
    dispatch(
      setCurrentSchemaDetails({
        ...schemaDetails,
        name: event.target.value
      })
    );
    validate();
  };

  const handleOptionsChange = event => {
    dispatch(
      setCurrentSchemaDetails({
        ...schemaDetails,
        option: event.target.value
      })
    );
    validate();
  };

  const toggleDefaultSchema = () => {
    dispatch(
      setCurrentSchemaDetails({
        ...schemaDetails,
        isDefaultSchema: !schemaDetails.isDefaultSchema
      })
    );
  };

  const handleObjectChange = object => {
    setSchemaObject({ ...object });
  };

  const handleBlur = (event, parentKey) => {
    formController.onBlur(event);
    validate();
    if (parentKey) {
      let key = parentKey.split('.');
      if (
        key.indexOf('eventTypeUniqueUpdateKey') >= 0 ||
        (key.indexOf('eventFields') >= 0 && key.indexOf('name') >= 0)
      ) {
        validateKeys();
      } else if (key.indexOf('eventTypeGroup') >= 0) {
        formState[parentKey] = { touched: true };
        setFormState({ ...formState });
      }
    }
  };

  const onFocus = e => {
    formController.onFocus(e);
  };

  const goNext = event => {
    let errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentSchemaDetails({
          ...schemaDetails
        })
      );
      dispatch(
        setCurrentSchema({
          ...schema,
          schemaName: schemaDetails.name,
          eventTypeDefault: schemaDetails.isDefaultSchema
        })
      );
      history.push(
        `${nextPath || event.currentTarget.attributes.link.value}${
          schemaId ? `/${schemaId}` : ''
        }`
      );
    }
  };

  const goBack = event => {
    dispatch(setCurrentSchema({ ...schemaObject }));
    dispatch(
      setCurrentSchemaDetails({
        ...schemaDetails,
        hasMovedToNextStep: true,
        name: schemaObject.schemaName,
        isDefaultSchema: schemaObject.eventTypeDefault
      })
    );
    history.push(
      `${backPath || event.currentTarget.attributes.link.value}${
        schemaId ? `/${schemaId}` : ''
      }`
    );
  };

  const saveSchema = () => {
    let errors = validate(true);
    let isValidJson = validateJsonPayload();
    let isValidKey = Object.entries(validateKeys()).length === 0;
    let isValidSchema = errors.meta.clean && isValidJson && isValidKey;
    if (schemaObject['internal']) {
      delete schemaObject['schemaEnrichmentData'];
    }
    if (schemaId) {
      dispatch(setCurrentSchema({ ...schemaObject }));
      if (isValidSchema) {
        dispatch(updateSchema({ schema: schemaObject, schemaId: schemaId }));
      }
    } else {
      let optionValues = schemaDetails.option.split(' - ');
      let currentSchema = {
        ...schemaObject,
        eventProductCategory: optionValues[0],
        eventProductName: optionValues[1],
        eventTypeCategory: optionValues[2],
        examplePayload: schemaObject['examplePayload']
      };
      dispatch(setCurrentSchema(currentSchema));
      if (isValidSchema) {
        dispatch(createSchema({ schema: currentSchema }));
      }
    }
    if (!isValidSchema) {
      dispatch(creatingSchemaFailed({ isValidationFailed: true }));
      Object.entries(errors)
        .filter(entry => entry[0] !== 'meta')
        .forEach(entry => (formState[entry[0]] = { touched: entry[1]?.showError }));
    }
  };

  const closeSchema = () => {
    formController = new FormController();
    history.push('/schema');
  };

  return {
    isLoading,
    schemaKeys,
    schemaId,
    schemaObject,
    schemaObjectCopy,
    validationErrors,
    isInvalidPayload,
    enrichmentProducts,
    disabledInputs,
    requiredFields,
    schemaSaveFailed,
    enrichmentSchemas,
    getCurrentSchemaKeys,
    navBackOnError,
    handleChange,
    handleOptionsChange,
    handleObjectChange,
    toggleDefaultSchema,
    updateCurrentErrorValidationSegment,
    validate,
    validateKeys,
    validateEventType,
    validateJsonPayload,
    shouldShowError,
    getErrorMessage,
    handleBlur,
    onFocus,
    goNext,
    goBack,
    saveSchema,
    closeSchema
  };
}
